import { zodResolver } from "@hookform/resolvers/zod";
import { creditProposalAddressSchema } from "@repo/core/helpers/zod.schemas";
import { ROUTES } from "@repo/core/routes";
import { useProposalFlowNavigation } from "../../utils/proposalFlowNavigation";
import { ViacepResource } from "@repo/core/services/resources/viacep";
import { viacepInstance } from "@repo/core/services/instances/viacep";
import { useCreditProposal } from "../hooks/useCreditProposal";
import { AddressDataFormType } from "@repo/core/stores/proposalForm/types";
import { useEffect, useMemo, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useDebounceCallback } from "usehooks-ts";
import { useQuery } from "@tanstack/react-query";
import { ibgeInstance } from "@repo/core/services/instances/ibge";
import { IbgeResource } from "@repo/core/services/resources/ibge";
import useToast from "@repo/core/components/core/Toast";
import { normalizeResidentialPhone2TipoToFormValue } from "@repo/core/utils/residentialPhone2.utils";
import {
  matchCityOptionValue,
  normalizeZipCodeForInput,
  resolveAddressPrimaryPhone,
  shouldClearAdditionalPhoneType
} from "./utils";
import { stripDigits } from "@repo/core/utils/mask.utils";
import type { ConsultProposalResponseDTO } from "@repo/core/services/entities/Proposals";
import { resolveCreditProposalMobilePhone } from "../hooks/useCreditProposal/mappers";
import {
  ADDITIONAL_PHONE_EQUALS_PRIMARY_MESSAGE,
  areNormalizedPhonesEqual,
  isCompleteAdditionalPhone,
  joinPhoneDigits,
  splitPhoneDigits
} from "@repo/core/utils/phone.utils";
import { apiInstance } from "@repo/core/services/instances/api";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import {
  PHONE_ALREADY_REGISTERED_MESSAGE,
  validatePhoneAgainstCpf
} from "@repo/core/services/validatePhoneAgainstCpf.service";
import { isValidCPF } from "@repo/core/helpers/zod.helpers";
import { getCleanCPF } from "@repo/core/utils/string.utils";

type CreditProposalAddressAliases = Partial<ConsultProposalResponseDTO> & {
  strEndDdd2?: string;
  strEndFone2?: string;
  strEndTipoFone2?: string;
  strCliente?: string;
};

const ibgeService = new IbgeResource(ibgeInstance);
const viacepService = new ViacepResource(viacepInstance);
const proposalsResource = new ProposalsResource(apiInstance);

const resolveProposalCpf = (
  proposalData?: CreditProposalAddressAliases | null
): string => getCleanCPF(proposalData?.strCpf || proposalData?.strCliente || "");

/** UI values stay unique so the select can distinguish Recados from Empresa. */
const residentialPhoneTypeOptions = [
  { label: "Remover seleção", value: "" },
  { label: "Convencional", value: "1" },
  { label: "Celular", value: "2" },
  { label: "Recados", value: "RECADOS" },
  { label: "Empresa", value: "EMPRESA" }
];

/** Joins DDD+Phone digits read from the proposal store back into a single masked input value. */
const joinPhone = (
  ddd?: string | null,
  number?: string | null
): string | undefined => {
  const dddDigits = (ddd || "").replace(/\D/g, "");
  const numberDigits = (number || "").replace(/\D/g, "");
  if (!dddDigits && !numberDigits) return undefined;
  return `${dddDigits}${numberDigits}`;
};

const resolveAdditionalPhone = (
  proposalData: CreditProposalAddressAliases
) => ({
  ddd: proposalData.strEndDdd2 ?? proposalData.strResDDD2,
  number: proposalData.strEndFone2 ?? proposalData.strResFone2,
  type: proposalData.strEndTipoFone2 ?? proposalData.strResTipoFone2
});

/** Splits a single masked/raw phone string into DDD (first 2 digits) and number (rest). */
const splitPhone = (raw?: string): { ddd: string; number: string } =>
  splitPhoneDigits(raw);

export const AddressDataFormController = () => {
  const { replace, completeStep, goToPreviousStep } =
    useProposalFlowNavigation();
  const toast = useToast();
  const {
    updateProposalData,
    proposalData,
    resetCreditProposalStore,
    persistFullProposalSnapshot
  } = useCreditProposal();
  const hasAutoFetched = useRef(false);
  const isSavingRef = useRef(false);
  const additionalPhoneValidationRef = useRef(0);

  // Get address data from proposalData
  const addressData = useMemo(() => {
    if (!proposalData) {
      return {
        zipCode: "",
        addressLine1: "",
        addressLine2: "",
        number: "",
        neighborhood: "",
        city: "",
        state: "",
        residentialPhone2: "",
        residentialPhone2Type: "",
        primaryPhone: ""
      };
    }

    const zipCode = normalizeZipCodeForInput(
      proposalData.strResCep || proposalData.strCep
    );
    const additionalPhone = resolveAdditionalPhone(proposalData);
    const primaryPhone = resolveCreditProposalMobilePhone(proposalData);

    return {
      zipCode,
      addressLine1:
        proposalData.strResEndereco || proposalData.strEndereco || "",
      addressLine2:
        proposalData.strResComplemento || proposalData.strComplemento || "",
      number: (
        proposalData.strResNumero ||
        proposalData.strNumero ||
        ""
      )
        .replace(/\D/g, "")
        .slice(0, 7),
      neighborhood: proposalData.strResBairro || proposalData.strBairro || "",
      city: proposalData.strResCidade || proposalData.strCidade || "",
      state: proposalData.strResUF || proposalData.strUf || "",
      residenceType: proposalData.strEndTipoResidencia || "",
      residentialPhone2:
        joinPhone(additionalPhone.ddd, additionalPhone.number) || "",
      residentialPhone2Type:
        normalizeResidentialPhone2TipoToFormValue(additionalPhone.type) || "",
      primaryPhone: resolveAddressPrimaryPhone(
        proposalData.strEndDdd1,
        proposalData.strEndFone1,
        joinPhoneDigits(primaryPhone.ddd, primaryPhone.phone)
      )
    };
  }, [proposalData]);

  const formValuesFromStore = useMemo(
    (): AddressDataFormType => ({
      ...addressData,
      residenceType:
        proposalData?.strEndTipoResidencia ||
        proposalData?.strResTipoResidencia ||
        ""
    }),
    [
      addressData,
      proposalData?.strEndTipoResidencia,
      proposalData?.strResTipoResidencia
    ]
  );

  const { control, handleSubmit, setValue, watch, reset, formState, setError, clearErrors, getValues } =
    useForm<AddressDataFormType>({
      resolver: zodResolver(creditProposalAddressSchema),
      mode: "onSubmit",
      reValidateMode: "onBlur",
      defaultValues: formValuesFromStore
    });

  const { isSubmitting } = formState;

  // Sync store -> form on load/back navigation only (not during submit).
  // Using `values` caused react-hook-form to reset mid-submit, clearing isSubmitting
  // and allowing a second POST /Proposta/Alterar.
  useEffect(() => {
    if (isSavingRef.current) return;
    reset(formValuesFromStore);
  }, [formValuesFromStore, reset]);

  const state = watch("state");
  const cityValue = watch("city");
  const residentialPhone2Value = watch("residentialPhone2");
  const residentialPhone2TypeValue = watch("residentialPhone2Type");
  const isResidentialPhone2TypeRequired = Boolean(
    stripDigits(residentialPhone2Value).length
  );

  useEffect(() => {
    if (
      shouldClearAdditionalPhoneType(
        residentialPhone2Value,
        residentialPhone2TypeValue
      )
    ) {
      setValue("residentialPhone2Type", "", {
        shouldDirty: true,
        shouldValidate: true
      });
    }
  }, [residentialPhone2TypeValue, residentialPhone2Value, setValue]);

  const handleAdditionalPhoneChange = useCallback(() => {
    additionalPhoneValidationRef.current += 1;
    clearErrors("residentialPhone2");
  }, [clearErrors]);

  const handleAdditionalPhoneBlur = useCallback(async () => {
    const rawPhone = getValues("residentialPhone2");
    const primaryPhone = getValues("primaryPhone");

    if (!stripDigits(rawPhone) || !isCompleteAdditionalPhone(rawPhone)) {
      return;
    }

    if (areNormalizedPhonesEqual(rawPhone, primaryPhone)) {
      setError("residentialPhone2", {
        type: "manual",
        message: ADDITIONAL_PHONE_EQUALS_PRIMARY_MESSAGE
      });
      return;
    }

    const { ddd, number } = splitPhone(rawPhone);
    const cleanCPF = resolveProposalCpf(proposalData);

    if (!ddd || !number || !cleanCPF || !isValidCPF(cleanCPF)) {
      return;
    }

    const requestId = ++additionalPhoneValidationRef.current;
    const result = await validatePhoneAgainstCpf(
      proposalsResource,
      ddd,
      number,
      cleanCPF
    );

    if (requestId !== additionalPhoneValidationRef.current) {
      return;
    }

    if (result.isValid) {
      clearErrors("residentialPhone2");
      return;
    }

    setError("residentialPhone2", {
      type: "manual",
      message: result.errorMessage || PHONE_ALREADY_REGISTERED_MESSAGE
    });
  }, [clearErrors, getValues, proposalData, setError]);

  const { data: states } = useQuery({
    queryKey: ["states"],
    queryFn: () => ibgeService.getStates()
  });

  const { data: cities } = useQuery({
    queryKey: ["cities", state],
    queryFn: () => ibgeService.getCities(state),
    enabled: !!state
  });

  const fetchAddress = async (cep: string) => {
    if (cep.length !== 8) return;

    try {
      const response = await viacepService.fetchCep(cep);

      if (response.data) {
        const zipCode = normalizeZipCodeForInput(response.data.cep);
        if (zipCode) {
          setValue("zipCode", zipCode);
        }
        setValue("addressLine1", response.data.logradouro);
        setValue("neighborhood", response.data.bairro);
        setValue("city", response.data.localidade);
        setValue("state", response.data.uf);
      }
    } catch (error) {
      console.error("Error fetching address:", error);
    }
  };

  const debouncedFetchAddress = useDebounceCallback(
    (cep: string) => fetchAddress(cep),
    500
  );

  const onChangeZipCode = (value: string) => {
    debouncedFetchAddress(stripDigits(value));
  };

  // Auto-fetch address when CEP already exists and is valid
  useEffect(() => {
    if (hasAutoFetched.current) return;

    const zipCode = normalizeZipCodeForInput(addressData.zipCode);

    if (zipCode.length === 8) {
      // Check if address fields are empty or incomplete
      const hasIncompleteAddress =
        !addressData.addressLine1 ||
        !addressData.neighborhood ||
        !addressData.city ||
        !addressData.state;

      if (hasIncompleteAddress) {
        hasAutoFetched.current = true;
        fetchAddress(zipCode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData.zipCode]);

  const { statesOptions, citiesOptions } = useMemo(() => {
    const statesOptions = states?.data?.map((state) => ({
      label: state.nome,
      value: state.sigla
    }));

    const citiesOptions = cities?.data?.map((city) => ({
      label: city.nome,
      value: city.nome
    }));

    return { statesOptions, citiesOptions };
  }, [states, cities]);

  const handleOnSubmitForm = handleSubmit(async (formData) => {
    if (isSavingRef.current || isSubmitting) {
      return;
    }

    const phone2 = splitPhone(formData.residentialPhone2);
    const cleanCPF = resolveProposalCpf(proposalData);

    if (
      phone2.ddd &&
      phone2.number &&
      cleanCPF &&
      isValidCPF(cleanCPF) &&
      !areNormalizedPhonesEqual(formData.residentialPhone2, formData.primaryPhone)
    ) {
      const result = await validatePhoneAgainstCpf(
        proposalsResource,
        phone2.ddd,
        phone2.number,
        cleanCPF
      );

      if (!result.isValid) {
        setError("residentialPhone2", {
          type: "manual",
          message: result.errorMessage || PHONE_ALREADY_REGISTERED_MESSAGE
        });
        return;
      }
    }

    isSavingRef.current = true;

    try {
      const matchedCity =
        citiesOptions?.length && formData.city
          ? (matchCityOptionValue(formData.city, citiesOptions) ??
            formData.city)
          : formData.city;

      if (proposalData) {
        const phone2 = splitPhone(formData.residentialPhone2);
        const hasAdditionalPhone = Boolean(phone2.number);
        const additionalPhoneType = hasAdditionalPhone
          ? formData.residentialPhone2Type || ""
          : "";
        const addressUpdate: CreditProposalAddressAliases = {
          strEndTipoResidencia:
            formData.residenceType ||
            proposalData.strEndTipoResidencia ||
            proposalData.strResTipoResidencia,
          strResCep:
            formData.zipCode || proposalData.strResCep || proposalData.strCep,
          strResEndereco:
            formData.addressLine1 ||
            proposalData.strResEndereco ||
            proposalData.strEndereco,
          strResComplemento:
            formData.addressLine2 ||
            proposalData.strResComplemento ||
            proposalData.strComplemento,
          strResNumero: (
            formData.number ||
            proposalData.strResNumero ||
            proposalData.strNumero ||
            ""
          )
            .replace(/\D/g, "")
            .slice(0, 7),
          strResBairro:
            formData.neighborhood ||
            proposalData.strResBairro ||
            proposalData.strBairro,
          strResCidade:
            matchedCity || proposalData.strResCidade || proposalData.strCidade,
          strResUF:
            formData.state || proposalData.strResUF || proposalData.strUf,
          strResPais: proposalData.strResPais || "Brasil",
          strEndDdd2: phone2.ddd,
          strEndFone2: phone2.number,
          strEndTipoFone2: additionalPhoneType,
          strResDDD2: phone2.ddd,
          strResFone2: phone2.number,
          strResTipoFone2: additionalPhoneType
        };

        updateProposalData(addressUpdate);
      }

      await persistFullProposalSnapshot();

      completeStep(ROUTES.creditProposalProfessionData);
    } catch {
      toast.error(
        "Erro ao salvar etapa",
        "Não foi possível salvar os dados desta etapa. Tente novamente."
      );
    } finally {
      isSavingRef.current = false;
    }
  });

  const handleGoBack = () => {
    goToPreviousStep(ROUTES.creditProposalPersonalData);
  };
  const handleClose = () => {
    resetCreditProposalStore();
    replace(ROUTES.home);
  };

  // When cities load, normalize city value to match an IBGE option (casing and accents)
  useEffect(() => {
    if (!cityValue?.trim() || !citiesOptions?.length) return;

    const matchedCity = matchCityOptionValue(cityValue, citiesOptions);
    if (matchedCity && matchedCity !== cityValue) {
      setValue("city", matchedCity, { shouldValidate: false });
    }
  }, [cityValue, citiesOptions, setValue]);

  return {
    control,
    handleOnSubmitForm,
    handleClose,
    handleGoBack,
    onChangeZipCode,
    statesOptions,
    citiesOptions,
    residentialPhoneTypeOptions,
    isResidentialPhone2TypeRequired,
    isSubmitting,
    handleAdditionalPhoneBlur,
    handleAdditionalPhoneChange
  };
};
